import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import useSlideshows, {
    ActivationTag,
    GallerySlideshow,
} from './hooks/useSlideshows';
import useWhitelabelSettings from '@/src/WhitelabelSettings/components/hooks/useWhitelabelSettings';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import { Button } from '@/src/shadComponents/ui/button';
import SmallLoader from '@/src/Common/components/SmallLoader';

interface SlideshowSettingsProps {
    galleryId: number;
    activationId: number;
    gallerySlug: string;
}

interface SlideshowDraft {
    id: number | null;
    name: string;
    tags: string[];
    matchMode: 'any' | 'all';
}

const emptyDraft: SlideshowDraft = {
    id: null,
    name: '',
    tags: [],
    matchMode: 'any',
};

function normalizeTag(tag: string): string {
    return tag.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Tag-scoped slideshows for a gallery. Each slideshow gets its own URL
 * (/gallery/:slug/slideshow/:id) showing only media carrying its tags, so one
 * gallery can drive e.g. a Team A wall and a Team B wall at the same event.
 * Tags can be picked from those already in use or typed free-form ahead of
 * the event.
 */
export default function SlideshowSettings({
    galleryId,
    activationId,
    gallerySlug,
}: SlideshowSettingsProps) {
    const {
        getSlideshows,
        createSlideshow,
        updateSlideshow,
        deleteSlideshow,
        getActivationTags,
    } = useSlideshows();
    const { getOrgDomain } = useWhitelabelSettings();
    const { org } = useUserOrg();

    const [isLoading, setIsLoading] = useState(true);
    const [slideshows, setSlideshows] = useState<GallerySlideshow[]>([]);
    const [knownTags, setKnownTags] = useState<ActivationTag[]>([]);
    const [galleryDomain, setGalleryDomain] = useState<string | null>(null);
    const [draft, setDraft] = useState<SlideshowDraft | null>(null);
    const [tagInput, setTagInput] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            try {
                const [slideshowList, tags, domain] = await Promise.all([
                    getSlideshows(galleryId),
                    getActivationTags(activationId),
                    getOrgDomain(org?.id),
                ]);

                if (isMounted) {
                    setSlideshows(slideshowList);
                    setKnownTags(tags);
                    setGalleryDomain(domain);
                }
            } catch (e) {
                console.error(e);
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        load();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [galleryId, activationId, org?.id]);

    const slideshowUrl = (slideshowId: number) => {
        const base = galleryDomain
            ? galleryDomain.endsWith('/')
                ? galleryDomain.slice(0, -1)
                : galleryDomain
            : '';

        return `${base}/gallery/${encodeURIComponent(
            gallerySlug,
        )}/slideshow/${slideshowId}`;
    };

    const suggestedTags = useMemo(() => {
        if (!draft) {
            return [];
        }

        return knownTags
            .map(({ tag }) => tag)
            .filter((tag) => !draft.tags.includes(tag));
    }, [knownTags, draft]);

    const addDraftTag = (rawTag: string) => {
        const tag = normalizeTag(rawTag);

        if (!draft || !tag || draft.tags.includes(tag)) {
            return;
        }

        setDraft({ ...draft, tags: [...draft.tags, tag] });
        setTagInput('');
    };

    const removeDraftTag = (tag: string) => {
        if (!draft) {
            return;
        }

        setDraft({ ...draft, tags: draft.tags.filter((t) => t !== tag) });
    };

    const saveDraft = async () => {
        if (!draft || draft.name.trim().length === 0) {
            toast.error('Slideshow name is required');
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                name: draft.name.trim(),
                tags: draft.tags,
                matchMode: draft.matchMode,
            };

            const saved = draft.id
                ? await updateSlideshow(draft.id, payload)
                : await createSlideshow(galleryId, payload);

            if (!saved) {
                toast.error('Failed to save slideshow');
                return;
            }

            setSlideshows((current) =>
                draft.id
                    ? current.map((s) => (s.id === saved.id ? saved : s))
                    : [...current, saved],
            );
            setDraft(null);
            setTagInput('');
            toast.success('Slideshow saved');
        } finally {
            setIsSaving(false);
        }
    };

    const removeSlideshow = async (slideshow: GallerySlideshow) => {
        if (
            !window.confirm(
                `Delete slideshow "${slideshow.name}"? Its URL will stop working.`,
            )
        ) {
            return;
        }

        const removed = await deleteSlideshow(slideshow.id);

        if (removed) {
            setSlideshows((current) =>
                current.filter((s) => s.id !== slideshow.id),
            );
            toast.success('Slideshow deleted');
        } else {
            toast.error('Failed to delete slideshow');
        }
    };

    const copyUrl = async (slideshowId: number) => {
        await navigator.clipboard.writeText(slideshowUrl(slideshowId));
        toast.success('Slideshow URL copied');
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-32">
                <SmallLoader />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        Slideshows
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Tag-filtered slideshows over this gallery. Each has its
                        own URL; images tagged at capture, by an experience
                        action, or in the media manager appear within seconds.
                    </p>
                </div>
                <Button
                    type="button"
                    onClick={() => {
                        setDraft({ ...emptyDraft });
                        setTagInput('');
                    }}
                >
                    New Slideshow
                </Button>
            </div>

            {slideshows.length === 0 && !draft && (
                <div className="text-sm text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                    No slideshows yet. The plain gallery slideshow (all photos)
                    is always available at{' '}
                    <span className="font-mono">
                        /gallery/{gallerySlug}/slideshow
                    </span>
                    .
                </div>
            )}

            <div className="space-y-3">
                {slideshows.map((slideshow) => (
                    <div
                        key={slideshow.id}
                        className="flex items-center justify-between border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                    >
                        <div className="min-w-0">
                            <div className="font-bold text-gray-900 dark:text-white">
                                {slideshow.name}
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-1">
                                {slideshow.tags.length === 0 ? (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        No tags — shows every photo
                                    </span>
                                ) : (
                                    <>
                                        {slideshow.tags.map((tag) => (
                                            <span
                                                key={tag}
                                                className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs text-gray-700 dark:text-gray-200"
                                            >
                                                {tag}
                                            </span>
                                        ))}
                                        {slideshow.tags.length > 1 && (
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                match{' '}
                                                {slideshow.matchMode === 'all'
                                                    ? 'all tags'
                                                    : 'any tag'}
                                            </span>
                                        )}
                                    </>
                                )}
                            </div>
                            <div className="mt-1 text-xs font-mono text-gray-500 dark:text-gray-400 truncate">
                                {slideshowUrl(slideshow.id)}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4 shrink-0">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => copyUrl(slideshow.id)}
                            >
                                Copy URL
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setDraft({
                                        id: slideshow.id,
                                        name: slideshow.name,
                                        tags: [...slideshow.tags],
                                        matchMode: slideshow.matchMode,
                                    });
                                    setTagInput('');
                                }}
                            >
                                Edit
                            </Button>
                            <Button
                                type="button"
                                variant="destructive"
                                onClick={() => removeSlideshow(slideshow)}
                            >
                                Delete
                            </Button>
                        </div>
                    </div>
                ))}
            </div>

            {draft && (
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 space-y-4">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                            Name
                        </label>
                        <input
                            type="text"
                            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                            placeholder="e.g. Team A Wall"
                            value={draft.name}
                            onChange={(e) =>
                                setDraft({ ...draft, name: e.target.value })
                            }
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                            Tags
                        </label>
                        <div className="flex flex-wrap items-center gap-1 mb-2">
                            {draft.tags.map((tag) => (
                                <span
                                    key={tag}
                                    className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs text-gray-700 dark:text-gray-200"
                                >
                                    {tag}
                                    <button
                                        type="button"
                                        className="hover:text-red-500"
                                        onClick={() => removeDraftTag(tag)}
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                            {draft.tags.length === 0 && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    No tags — the slideshow shows every photo
                                </span>
                            )}
                        </div>
                        <input
                            type="text"
                            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                            placeholder="Type a tag and press Enter"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addDraftTag(tagInput);
                                }
                            }}
                        />
                        {suggestedTags.length > 0 && (
                            <div className="mt-2 flex flex-wrap items-center gap-1">
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    In use:
                                </span>
                                {suggestedTags.map((tag) => (
                                    <button
                                        key={tag}
                                        type="button"
                                        className="inline-flex items-center rounded-full border border-gray-300 dark:border-gray-600 px-2 py-0.5 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        onClick={() => addDraftTag(tag)}
                                    >
                                        + {tag}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {draft.tags.length > 1 && (
                        <div>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                                Match
                            </label>
                            <select
                                className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                                value={draft.matchMode}
                                onChange={(e) =>
                                    setDraft({
                                        ...draft,
                                        matchMode: e.target.value as
                                            | 'any'
                                            | 'all',
                                    })
                                }
                            >
                                <option value="any">
                                    Any tag (photo has at least one)
                                </option>
                                <option value="all">
                                    All tags (photo has every one)
                                </option>
                            </select>
                        </div>
                    )}

                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            onClick={saveDraft}
                            disabled={isSaving}
                        >
                            {draft.id ? 'Save Slideshow' : 'Create Slideshow'}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setDraft(null)}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
